import {
  createImportedBodyOwnershipLookupKeys,
  resolveImportedBodyDefinitionBindingId,
} from '../../imported-model/utils'
import type {
  PuppyDisplayNodeBinding,
  PuppyDisplayNodeBodyBinding,
} from './types'

/**
 * 离线包 bodyBinding 候选索引。
 *
 * 设计说明：
 * `null` 表示同一 owner 与查询键命中了多个导出 bodyBindingId。调用方不能任选一个结果，
 * 否则同源零部件的不同 occurrence 会被错误合并到同一个交互目标。
 */
export interface PuppyBodyBindingReferenceIndex {
  /** owner assemblyNodeId + body 查询键到最终 bodyBindingId 的确定性映射。 */
  readonly bodyBindingIdByOwnerLookupKey: Map<string, string | null>
  /** owner assemblyNodeId + body 查询键 + bodyObject 查询键到最终 bodyBindingId 的确定性映射。 */
  readonly bodyBindingIdByOwnerBodyObjectLookupKey: Map<string, string | null>
}

/**
 * 最终离线包 bodyBindingId 的解析输入。
 */
export interface PuppyBodyBindingReferenceQuery {
  /** body 所属的稳定装配节点 ID。 */
  readonly assemblyNodeId: string
  /** 运行时、源定义或 occurrence 级 bodyBindingId。 */
  readonly bodyBindingId: string
  /** 用于在同源 body 之间消除歧义的 Worker/选择层对象 ID。 */
  readonly bodyObjectIds?: readonly (string | null | undefined)[]
}

/**
 * 创建带长度前缀的复合键，避免普通字符串拼接造成碰撞。
 *
 * @param parts 复合键片段。
 * @returns 可安全写入 Map 的复合键。
 */
function createCompositeLookupKey(parts: readonly string[]): string {
  return parts.map((part) => `${part.length}:${part}`).join('|')
}

/**
 * 向键列表追加非空且去重后的字符串。
 *
 * @param target 目标键列表。
 * @param value 候选键。
 */
function appendUniqueStringKey(target: string[], value: string | null | undefined): void {
  const normalizedValue = value?.trim()
  if (normalizedValue && !target.includes(normalizedValue)) {
    target.push(normalizedValue)
  }
}

/**
 * 向键列表追加 imported body 身份协议提供的全部等价键。
 *
 * @param target 目标键列表。
 * @param bodyBindingId bodyBindingId、bodyId 或几何对象 ID。
 */
function appendBodyOwnershipLookupKeys(
  target: string[],
  bodyBindingId: string | null | undefined,
): void {
  createImportedBodyOwnershipLookupKeys(bodyBindingId).forEach((key) => {
    appendUniqueStringKey(target, key)
  })
}

/**
 * 创建最终导出 bodyBinding 可被运行时引用命中的候选键。
 *
 * @param bodyBinding 最终显示节点 body 绑定。
 * @returns body 身份候选键列表。
 */
function createExportedBodyBindingLookupKeys(
  bodyBinding: PuppyDisplayNodeBodyBinding,
): readonly string[] {
  const keys: string[] = []
  appendBodyOwnershipLookupKeys(keys, bodyBinding.bodyBindingId)
  appendBodyOwnershipLookupKeys(
    keys,
    resolveImportedBodyDefinitionBindingId(bodyBinding.bodyBindingId),
  )
  appendBodyOwnershipLookupKeys(keys, bodyBinding.bodyId)
  return keys
}

/**
 * 创建最终导出 body 的对象身份候选键。
 *
 * @param bodyBinding 最终显示节点 body 绑定。
 * @returns bodyObject 候选键列表。
 */
function createExportedBodyObjectLookupKeys(
  bodyBinding: PuppyDisplayNodeBodyBinding,
): readonly string[] {
  const keys: string[] = []
  appendUniqueStringKey(keys, bodyBinding.geometryObjectId)
  appendBodyOwnershipLookupKeys(keys, bodyBinding.geometryObjectId)
  appendUniqueStringKey(keys, bodyBinding.bodyId)
  appendBodyOwnershipLookupKeys(keys, bodyBinding.bodyId)
  appendBodyOwnershipLookupKeys(keys, bodyBinding.bodyBindingId)
  appendBodyOwnershipLookupKeys(
    keys,
    resolveImportedBodyDefinitionBindingId(bodyBinding.bodyBindingId),
  )
  return keys
}

/**
 * 创建原始 bodyBinding 引用的查询键。
 *
 * @param bodyBindingId 原始 bodyBindingId。
 * @returns body 身份查询键列表。
 */
function createBodyBindingQueryKeys(bodyBindingId: string): readonly string[] {
  const keys: string[] = []
  appendBodyOwnershipLookupKeys(keys, bodyBindingId)
  appendBodyOwnershipLookupKeys(keys, resolveImportedBodyDefinitionBindingId(bodyBindingId))
  return keys
}

/**
 * 创建原始 bodyObject 引用的查询键。
 *
 * @param bodyObjectIds Worker、拓扑快照或选择层对象 ID。
 * @returns bodyObject 查询键列表。
 */
function createBodyObjectQueryKeys(
  bodyObjectIds: readonly (string | null | undefined)[],
): readonly string[] {
  const keys: string[] = []
  bodyObjectIds.forEach((bodyObjectId) => {
    appendUniqueStringKey(keys, bodyObjectId)
    appendBodyOwnershipLookupKeys(keys, bodyObjectId)
  })
  return keys
}

/**
 * 写入唯一候选；同一键对应多个最终 bodyBindingId 时标记歧义。
 *
 * @param target 候选 Map。
 * @param lookupKey 查询键。
 * @param exportedBodyBindingId 最终 bodyBindingId。
 */
function writeUniqueBodyBindingCandidate(
  target: Map<string, string | null>,
  lookupKey: string,
  exportedBodyBindingId: string,
): void {
  const existingBodyBindingId = target.get(lookupKey)
  if (existingBodyBindingId === undefined) {
    target.set(lookupKey, exportedBodyBindingId)
    return
  }

  // 多个 occurrence 命中同一键时保留歧义状态，禁止静默选择错误组件。
  if (existingBodyBindingId !== exportedBodyBindingId) {
    target.set(lookupKey, null)
  }
}

/**
 * 写入 owner 级 bodyBinding 候选。
 *
 * @param index 候选索引。
 * @param assemblyNodeId body owner 装配节点 ID。
 * @param bodyLookupKey body 查询键。
 * @param exportedBodyBindingId 最终 bodyBindingId。
 */
function writeOwnerBodyBindingCandidate(
  index: PuppyBodyBindingReferenceIndex,
  assemblyNodeId: string,
  bodyLookupKey: string,
  exportedBodyBindingId: string,
): void {
  writeUniqueBodyBindingCandidate(
    index.bodyBindingIdByOwnerLookupKey,
    createCompositeLookupKey([assemblyNodeId, bodyLookupKey]),
    exportedBodyBindingId,
  )
}

/**
 * 写入 owner + bodyObject 级 bodyBinding 候选。
 *
 * @param index 候选索引。
 * @param assemblyNodeId body owner 装配节点 ID。
 * @param bodyLookupKey body 查询键。
 * @param bodyObjectLookupKey bodyObject 查询键。
 * @param exportedBodyBindingId 最终 bodyBindingId。
 */
function writeOwnerBodyObjectBindingCandidate(
  index: PuppyBodyBindingReferenceIndex,
  assemblyNodeId: string,
  bodyLookupKey: string,
  bodyObjectLookupKey: string,
  exportedBodyBindingId: string,
): void {
  writeUniqueBodyBindingCandidate(
    index.bodyBindingIdByOwnerBodyObjectLookupKey,
    createCompositeLookupKey([assemblyNodeId, bodyLookupKey, bodyObjectLookupKey]),
    exportedBodyBindingId,
  )
}

/**
 * 基于最终显示节点绑定创建可复用的 bodyBinding 候选索引。
 *
 * @param nodeBindings 最终写入离线包的显示节点绑定。
 * @returns owner 级候选索引。
 */
export function createPuppyBodyBindingReferenceIndex(
  nodeBindings: readonly PuppyDisplayNodeBinding[],
): PuppyBodyBindingReferenceIndex {
  const index: PuppyBodyBindingReferenceIndex = {
    bodyBindingIdByOwnerLookupKey: new Map(),
    bodyBindingIdByOwnerBodyObjectLookupKey: new Map(),
  }

  nodeBindings.forEach((nodeBinding) => {
    const assemblyNodeId = nodeBinding.assemblyNodeId?.trim()
    if (!assemblyNodeId) {
      return
    }

    // 同一显示节点可能承载多个 body，必须为每个 body 建立独立的歧义感知索引。
    nodeBinding.bodyBindings.forEach((bodyBinding) => {
      const bodyLookupKeys = createExportedBodyBindingLookupKeys(bodyBinding)
      const bodyObjectLookupKeys = createExportedBodyObjectLookupKeys(bodyBinding)
      bodyLookupKeys.forEach((bodyLookupKey) => {
        writeOwnerBodyBindingCandidate(
          index,
          assemblyNodeId,
          bodyLookupKey,
          bodyBinding.bodyBindingId,
        )
        bodyObjectLookupKeys.forEach((bodyObjectLookupKey) => {
          writeOwnerBodyObjectBindingCandidate(
            index,
            assemblyNodeId,
            bodyLookupKey,
            bodyObjectLookupKey,
            bodyBinding.bodyBindingId,
          )
        })
      })
    })
  })
  return index
}

/**
 * 解析引用在最终离线包中的 occurrence 级 bodyBindingId。
 *
 * 解析顺序先使用 bodyObject 上下文消除同源 body 歧义，再退回 owner + body 身份；
 * 任一层级存在歧义或没有命中时都返回 `null`，由调用方决定报错或保留原值。
 *
 * @param index 最终显示节点绑定索引。
 * @param query 原始 body 引用。
 * @returns 可确定的最终 bodyBindingId；无法确定时返回 `null`。
 */
export function resolvePuppyExportedBodyBindingId(
  index: PuppyBodyBindingReferenceIndex,
  query: PuppyBodyBindingReferenceQuery,
): string | null {
  const assemblyNodeId = query.assemblyNodeId.trim()
  const bodyBindingId = query.bodyBindingId.trim()
  if (!assemblyNodeId || !bodyBindingId) {
    return null
  }

  const bodyLookupKeys = createBodyBindingQueryKeys(bodyBindingId)
  const bodyObjectLookupKeys = createBodyObjectQueryKeys(query.bodyObjectIds ?? [])
  for (const bodyLookupKey of bodyLookupKeys) {
    for (const bodyObjectLookupKey of bodyObjectLookupKeys) {
      const exportedBodyBindingId = index.bodyBindingIdByOwnerBodyObjectLookupKey.get(
        createCompositeLookupKey([assemblyNodeId, bodyLookupKey, bodyObjectLookupKey]),
      )
      if (exportedBodyBindingId) {
        return exportedBodyBindingId
      }
    }
  }

  // bodyObject 缺失或未命中时，仅在 owner 内的 body 身份唯一时允许回退。
  for (const bodyLookupKey of bodyLookupKeys) {
    const exportedBodyBindingId = index.bodyBindingIdByOwnerLookupKey.get(
      createCompositeLookupKey([assemblyNodeId, bodyLookupKey]),
    )
    if (exportedBodyBindingId) {
      return exportedBodyBindingId
    }
  }
  return null
}
